/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import type {
  PropsWithChildren,
  ReactElement,
} from 'react';

import useOnClickOutside from 'use-onclickoutside';

import styled from 'styled-components';

import type {
  GetOptionLabel,
  GetOptionValue,
  GroupBase,
  OnChangeValue,
} from 'react-select';

import { texts } from '../ReactSelectExtended/texts';
import { useFilterStyles } from '../ReactSelectExtended/filterStyles';

import type {
  OptionTypeBase,
  FilterSelectBaseProps,
} from '../ReactSelectExtended/types';

const StyledWrapper = styled.div({
  position: 'relative',
});

export const StyledButtonWrapper = styled.div<{
  $isDisabled: boolean;
  $isOpen: boolean;
}>(({
  $isDisabled,
  $isOpen,

  theme: {
    colors,
    input,
  },
}) => ({
  display: 'flex',
  boxSizing: 'border-box',
  alignItems: 'stretch',
  cursor: $isDisabled ? 'default' : 'pointer',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  outline: 0,
  position: 'relative',
  transition: 'all 100ms',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: $isDisabled
    ? colors.NEUTRAL_INACTIVE
    : (
      $isOpen
        ? colors[input.BORDER_COLOR_FOCUS]
        : colors[input.BORDER_COLOR]
    ),
  color: colors.NEUTRAL_INPUT,

  backgroundColor: $isDisabled
    ? colors[input.BG_COLOR_DISABLED]
    : (
      $isOpen
        ? colors[input.BG_COLOR_FOCUS]
        : colors[input.BG_COLOR]
    ),

  height: input.HEIGHT_SMALL,
  borderRadius: input.BORDER_RADIUS,

  '&:hover': ($isDisabled || $isOpen) ? undefined : {
    borderColor: colors[input.BORDER_COLOR_HOVER],
    color: colors.ACTION_HOVER,
  },

  '&:active': ($isDisabled || $isOpen) ? undefined : {
    color: colors.ACTION_PRESSED,
  },
}));

export type ButtonProps = {
  disabled: boolean;
  onClick: () => void;
  children: ReactElement<
  PropsWithChildren,
  typeof StyledLabel
  >;
};

export const StyledButton = styled.button(({
  theme: {
    fonts,
  },
}) => ({
  ...fonts.CAPTION,
  flex: 1,
  margin: 0,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  textAlign: 'left',
  cursor: 'inherit',
  overflow: 'hidden',
  paddingTop: 0,
  paddingRight: 7,
  paddingBottom: 0,
  paddingLeft: 7,
}));

export const StyledLabel = styled.div(({
  theme: {
    colors,
    input,
  },
}) => ({
  color: colors[input.TEXT_COLOR],
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}));

export const StyledPlaceholder = styled.div(({
  theme: {
    colors,
    input,
  },
}) => ({
  color: colors[input.PLACEHOLDER_COLOR],
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}));

const StyledArrowBase = styled.button(({
  disabled,

  theme: {
    colors,
  },
}) => ({
  margin: 0,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  paddingTop: 3,
  paddingBottom: 3,
  paddingLeft: 7,
  paddingRight: 7,
  transition: 'color 150ms ease 0s',
  minWidth: 36,
  cursor: 'inherit',

  '&:hover': disabled ? undefined : {
    color: colors.ACTION_HOVER,
  },

  '&:active': disabled ? undefined : {
    color: colors.ACTION_PRESSED,
  },
}));

export type ArrowProps = {
  disabled: boolean;
  isOpen: boolean;
  onClick: () => void;
};

export const StyledArrow = styled(StyledArrowBase)<{
  disabled: boolean;
  isOpen: boolean;
}>(({
  disabled,
  isOpen,

  theme: {
    colors,
  },
}) => ({
  color: isOpen ? colors.ACTION_DEFAULT : colors.NEUTRAL_INPUT,

  [`${StyledButtonWrapper}:hover &`]: (disabled || isOpen) ? undefined : {
    color: colors.ACTION_DEFAULT,
  },

  [`${StyledButtonWrapper}:active &`]: (disabled || isOpen) ? undefined : {
    color: colors.ACTION_DEFAULT,
  },
}));

export type ClearButtonProps = {
  onClick: () => void;
};

export const StyledClearButton = styled.button(({
  theme: {
    colors,
  },
}) => ({
  margin: 0,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  paddingTop: 3,
  paddingBottom: 3,
  paddingLeft: 7,
  paddingRight: 0,
  transition: 'color 150ms ease 0s',
  color: colors.NEUTRAL_INPUT,
  minWidth: 23,
  cursor: 'inherit',

  '&:hover': {
    color: colors.ACTION_HOVER,
  },

  '&:active': {
    color: colors.ACTION_PRESSED,
  },
}));

export const StyledMenu = styled.div({
  position: 'absolute',
  minWidth: 256,
  zIndex: 2,
  marginTop: 8,
  marginBottom: 8,
});

const toggle = (isOpen: boolean): boolean => !isOpen;

const MULTI_VALUE_SEPARATOR = ', ';

export function FilterSelectBase<
OptionType extends OptionTypeBase,
IsMulti extends boolean,
Group extends GroupBase<OptionType>,
>(
  props: FilterSelectBaseProps<OptionType, IsMulti, Group>,
): ReactElement {
  const {
    isClearable = true,
    isDisabled = false,
    value = null,
    labelKey = 'label',
    valueKey = 'value',
    isMulti = false,
    mainPlaceholder = 'Выберите...',
    onChange = undefined,
    renderSelect,
    getOptionLabel: getOptionLabelProp = undefined,
    getOptionValue: getOptionValueProp = undefined,
  } = props;

  const filterStyles = useFilterStyles();

  const [isOpen, setIsOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);

  const onClickOutside = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  const toggleIsOpen = useCallback(() => {
    setIsOpen(toggle);
  }, []);

  useOnClickOutside(rootRef, onClickOutside);

  const getOptionLabel = useCallback<GetOptionLabel<OptionType>>((option) => {
    if (getOptionLabelProp) {
      return getOptionLabelProp(option);
    }

    if (!option) {
      return '';
    }

    return option[labelKey] as string;
  }, [getOptionLabelProp, labelKey]);

  const getOptionValue = useCallback<GetOptionValue<OptionType>>((option) => {
    if (getOptionValueProp) {
      return getOptionValueProp(option);
    }

    if (!option) {
      return '';
    }

    return option[valueKey] as string;
  }, [getOptionValueProp, valueKey]);

  const hasValue = useMemo<boolean>(
    () => {
      if (!value) {
        return false;
      }

      if (isMulti) {
        if (value instanceof Array) {
          return value.length > 0;
        }

        return false;
      }

      return true;
    },

    [value, isMulti],
  );

  const label = useMemo<string | null>(
    () => {
      if (!hasValue) {
        return null;
      }

      if (isMulti) {
        if (value instanceof Array) {
          return (value as OptionType[])
            .map(getOptionLabel)
            .join(MULTI_VALUE_SEPARATOR);
        }

        return '';
      }

      return getOptionLabel(value as OptionType);
    },

    [hasValue, value, isMulti, getOptionLabel],
  );

  const handleClear = useCallback(() => {
    setIsOpen(false);

    if (onChange) {
      onChange((isMulti ? [] : null) as unknown as OnChangeValue<OptionType, IsMulti>);
    }
  }, [isMulti, onChange]);

  const onSelectChange = useCallback((nextValue: OnChangeValue<OptionType, IsMulti>) => {
    if (!isMulti) {
      setIsOpen(false);
    }

    if (onChange) {
      onChange(nextValue);
    }
  }, [onChange, isMulti]);

  return (
    <StyledWrapper ref={rootRef}>
      <StyledButtonWrapper
        $isDisabled={isDisabled}
        $isOpen={isOpen}
      >
        <StyledButton
          type="button"
          disabled={isDisabled}
          onClick={toggleIsOpen}
          title={label || ''}
        >
          {
            label ? (
              <StyledLabel>
                {label}
              </StyledLabel>
            ) : (
              <StyledPlaceholder>
                {mainPlaceholder}
              </StyledPlaceholder>
            )
          }
        </StyledButton>

        {
          isClearable && !isDisabled && hasValue && (
            <StyledClearButton
              onClick={handleClear}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.6673 4.27337L11.7273 3.33337L8.00065 7.06004L4.27398 3.33337L3.33398 4.27337L7.06065 8.00004L3.33398 11.7267L4.27398 12.6667L8.00065 8.94004L11.7273 12.6667L12.6673 11.7267L8.94065 8.00004L12.6673 4.27337Z"
                />
              </svg>
            </StyledClearButton>
          )
        }

        <StyledArrow
          type="button"
          disabled={isDisabled}
          isOpen={isOpen}
          onClick={toggleIsOpen}
        >
          {
            isOpen ? (
              <svg
                width={20}
                height={20}
                viewBox="0 0 20 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 6.9126L5 11.9126L6.175 13.0876L10 9.27093L13.825 13.0876L15 11.9126L10 6.9126Z"
                />
              </svg>
            ) : (
              <svg
                width={20}
                height={20}
                viewBox="0 0 20 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.825 6.9126L10 10.7293L6.175 6.9126L5 8.0876L10 13.0876L15 8.0876L13.825 6.9126Z"
                />
              </svg>
            )
          }
        </StyledArrow>
      </StyledButtonWrapper>

      {
        isOpen && (
          <StyledMenu>
            {
              renderSelect({
                ...texts,
                styles: filterStyles,
                ...props,
                autoFocus: true,
                menuIsOpen: true,
                controlShouldRenderValue: false,
                hideSelectedOptions: !isMulti,
                isClearable: false,
                onChange: onSelectChange,
                getOptionLabel,
                getOptionValue,
              })
            }
          </StyledMenu>
        )
      }
    </StyledWrapper>
  );
}
